import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Warning, Trash, ShieldCheck, FilePdf, Download } from "@phosphor-icons/react";
import { userLabel } from "@/lib/helpers";
import { FileUpload } from "@/components/ui/file-upload";

// Curated India-first list — matches the common credentials for special-ed therapists.
const CERT_PRESETS = [
  "RCI Registration (CRR/CRE)",
  "Background Verification",
  "CPR / First Aid",
  "BCBA / RBT",
  "PECS Level 1",
  "PECS Level 2",
  "DIR / Floortime",
  "Hanen It Takes Two To Talk",
  "Sensory Integration Certification",
  "AAC Specialist Training",
];

export default function CertificationsPage() {
  const [certs, setCerts] = useState([]);
  const [buckets, setBuckets] = useState({ expired: [], d30: [], d60: [], d90: [] });
  const [therapists, setTherapists] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    therapist_id: "", name: "", issuing_body: "", reference_no: "",
    issued_on: "", expires_on: "", document_url: "", document_file_id: null,
    verified: false, notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const [c, b, u] = await Promise.all([
        api.get("/certifications"),
        api.get("/certifications/expiring?days=90"),
        api.get("/users"),
      ]);
      setCerts(c.data || []);
      setBuckets(b.data || { expired: [], d30: [], d60: [], d90: [] });
      setTherapists((u.data || []).filter(x => (x.roles || []).includes("therapist")));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      therapist_id: c.therapist_id, name: c.name || "",
      issuing_body: c.issuing_body || "", reference_no: c.reference_no || "",
      issued_on: c.issued_on || "", expires_on: c.expires_on || "",
      document_url: c.document_url || "",
      document_file_id: c.document_file_id || null,
      verified: !!c.verified, notes: c.notes || "",
    });
    setShow(true);
  };
  const submit = async () => {
    if (!form.therapist_id || !form.name.trim()) { toast.error("Therapist and name are required"); return; }
    try {
      if (editing) {
        const patch = { ...form }; delete patch.therapist_id;
        await api.put(`/certifications/${editing.id}`, patch);
        toast.success("Certification updated");
      } else {
        await api.post("/certifications", form);
        toast.success("Certification added");
      }
      setShow(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this certification?")) return;
    try { await api.delete(`/certifications/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const therapistName = (id) => therapists.find(t => t.id === id)?.name || id;

  const bucketPanel = (title, list, tone) => list.length > 0 && (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-display tracking-tight flex items-center gap-2">
          <Warning size={16} className={tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : "text-muted-foreground"} weight="duotone" />
          {title} ({list.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {list.map(c => (
            <div key={c.id} className="px-5 py-3 flex items-center gap-3 text-sm">
              <div className="flex-1">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{therapistName(c.therapist_id)}{c.reference_no && ` · ${c.reference_no}`}</div>
              </div>
              <div className="text-right">
                <div className={`text-xs font-medium ${c.days_to_expiry < 0 ? "text-red-600" : c.days_to_expiry <= 30 ? "text-amber-700" : "text-muted-foreground"}`}>
                  {c.days_to_expiry < 0 ? `Expired ${Math.abs(c.days_to_expiry)}d ago` : `Expires in ${c.days_to_expiry}d`}
                </div>
                <div className="text-[10px] text-muted-foreground">{c.expires_on}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1.5">Certifications</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Therapist compliance</h1>
          <p className="text-sm text-muted-foreground mt-1">Track RCI registration, BGV, CPR/first-aid, BCBA/RBT and other credentials with expiry alerts.</p>
        </div>
        <Button size="lg" className="gap-1.5 w-full sm:w-auto" onClick={openNew} data-testid="new-cert-btn"><Plus size={16} weight="bold" /> New certification</Button>
      </div>

      {bucketPanel("Already expired", buckets.expired, "red")}
      {bucketPanel("Expires in 30 days", buckets.d30, "amber")}
      {bucketPanel("Expires in 60 days", buckets.d60, "amber")}
      {bucketPanel("Expires in 90 days", buckets.d90, "muted")}

      <Card>
        <CardHeader><CardTitle className="text-base font-display tracking-tight">All certifications ({certs.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {certs.length === 0 ? (
            <div className="px-5 py-14 text-center text-sm text-muted-foreground">
              <ShieldCheck size={40} weight="duotone" className="mx-auto text-muted-foreground/60 mb-3" />
              No certifications recorded yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {certs.map(c => (
                <div key={c.id} className="px-5 py-3 flex items-center gap-3 text-sm flex-wrap sm:flex-nowrap" data-testid={`cert-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {c.name}
                      {c.verified && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Verified</Badge>}
                      {c.document_file_id && (
                        <a
                          href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/files/${c.document_file_id}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                          data-testid={`cert-doc-download-${c.id}`}
                        >
                          <Download size={10} weight="bold" /> Document
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{therapistName(c.therapist_id)}{c.issuing_body && ` · ${c.issuing_body}`}{c.reference_no && ` · ${c.reference_no}`}</div>
                  </div>
                  {c.expires_on && (
                    <div className="text-right">
                      <div className={`text-xs font-medium ${c.days_to_expiry < 0 ? "text-red-600" : c.days_to_expiry <= 30 ? "text-amber-700" : c.days_to_expiry <= 90 ? "text-amber-600" : "text-muted-foreground"}`}>
                        {c.days_to_expiry != null && (c.days_to_expiry < 0 ? `Expired` : `${c.days_to_expiry}d left`)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{c.expires_on}</div>
                    </div>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-red-600 hover:text-red-700"><Trash size={14} /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={show} onOpenChange={(o) => { setShow(o); if (!o) setEditing(null); }}>
        <DialogContent className="bg-card max-w-xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit certification" : "New certification"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Therapist</Label>
              <Select value={form.therapist_id} onValueChange={v => setForm(f => ({ ...f, therapist_id: v }))} disabled={!!editing}>
                <SelectTrigger data-testid="cert-therapist"><SelectValue placeholder="Pick a therapist" /></SelectTrigger>
                <SelectContent className="bg-card">
                  {therapists.map(t => <SelectItem key={t.id} value={t.id}>{userLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Certification</Label>
              <Input list="cert-presets" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. RCI Registration (CRR/CRE)" data-testid="cert-name" />
              <datalist id="cert-presets">
                {CERT_PRESETS.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Issuing body</Label><Input value={form.issuing_body} onChange={e => setForm(f => ({ ...f, issuing_body: e.target.value }))} placeholder="e.g. RCI" /></div>
              <div className="space-y-1.5"><Label>Reference / cert no</Label><Input value={form.reference_no} onChange={e => setForm(f => ({ ...f, reference_no: e.target.value }))} className="font-mono" /></div>
              <div className="space-y-1.5"><Label>Issued on</Label><Input type="date" value={form.issued_on} onChange={e => setForm(f => ({ ...f, issued_on: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Expires on</Label><Input type="date" value={form.expires_on} onChange={e => setForm(f => ({ ...f, expires_on: e.target.value }))} data-testid="cert-expires" /></div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Certificate document <span className="text-[10px] text-muted-foreground">(PDF or image, optional)</span></Label>
                <div className="flex flex-wrap items-center gap-3">
                  <FileUpload
                    entityType="certification"
                    entityId={editing?.id}
                    variant="attachment"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(id) => setForm(f => ({ ...f, document_file_id: id }))}
                  />
                  {form.document_file_id && (
                    <a
                      href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/files/${form.document_file_id}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                      data-testid="cert-doc-open"
                    >
                      <FilePdf size={14} weight="duotone" /> Open uploaded document
                    </a>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Or paste a document URL <span className="text-[10px] text-muted-foreground">(external link)</span></Label><Input value={form.document_url} onChange={e => setForm(f => ({ ...f, document_url: e.target.value }))} placeholder="https://…" /></div>
              <label className="flex items-center gap-2 sm:col-span-2 text-sm">
                <Checkbox checked={form.verified} onCheckedChange={v => setForm(f => ({ ...f, verified: !!v }))} data-testid="cert-verified" />
                <span>Document verified by admin</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="cert-save">{editing ? "Save changes" : "Add certification"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
